<template>
  <div
    class="mx-auto flex w-full max-w-container flex-1 flex-col items-center justify-center gap-x-6 px-5 lg:flex-row lg:gap-y-3"
  >
    <div class="shrink-0 items-center gap-3" :class="props.scrolled ? 'hidden lg:flex' : 'flex'">
      <img class="h-11" :src="gameLogo" :alt="game?.key ? game.key + ' logo' : ''" />
      <div class="flex gap-3">
        <div
          v-for="supportedPlatform in supportedPlatforms"
          :key="supportedPlatform"
          class="border-gp-rhino from-gp-midnight-gradient-a to-gp-midnight-gradient-b bg-linear-to-b h-9 rounded-lg border p-2 text-center font-bold leading-4 text-white opacity-80"
        >
          <div v-if="imageExists(supportedPlatform) && !brokenLogos.has(supportedPlatform)">
            <img
              :src="getImageSource(supportedPlatform)"
              :alt="supportedPlatform.toLowerCase()"
              class="h-4"
              @error="brokenLogos.add(supportedPlatform)"
            />
          </div>
          <div v-else>
            {{ supportedPlatform }}
          </div>
        </div>
      </div>
    </div>
    <h1
      data-testid="header-servername"
      class="text-tracking-wide max-w-full text-ellipsis text-center text-2xl font-bold lg:text-left lg:text-4xl"
      :class="props.scrolled ? 'line-clamp-1' : 'line-clamp-2'"
    >
      {{ gameServerState?.name || server?.serverName }}
    </h1>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useServiceStore } from "../../store/service";

const service = useServiceStore();

const props = defineProps<{ scrolled: boolean }>();

const server = computed(() => service.ctx.sys?.gameServer);

const game = computed(() => service.ctx.sys?.game);

const supportedPlatforms = computed(() => {
  if (game.value?.supportedPlatforms?.length) {
    return game.value.supportedPlatforms;
  } else if (game.value?.platform === "Crossplay") {
    return ["PS4", "Xbox"];
  } else {
    return [game.value?.platform];
  }
});

// The badges are platform *families* rather than console generations: one PlayStation logo covers
// PS4 and PS5, one Nintendo logo covers Switch and Switch 2. A logo says it faster than naming a
// version, and it does not go stale when the next console ships.
const PLATFORM_LOGOS = ["Xbox", "PS4", "Switch"];

const imageExists = (platform: string) => Boolean(platform) && PLATFORM_LOGOS.includes(platform);

// A logo the CDN does not have yet degrades to the platform's name rather than a broken-image icon,
// so a new platform can be added to a game entry before its artwork is uploaded.
const brokenLogos = reactive(new Set<string>());

const getImageSource = (platform: string) => {
  return `https://cdn.g-portal.com/images/platforms/${platform?.toLowerCase()}.png`;
};

const gameServerState = computed(() => service.gameServerState);
const gameLogo = computed(() =>
  game.value?.key ? `https://cdn.g-portal.com/images/games/${game.value.key}/logo.png` : undefined,
);
</script>
